//frontend/dispatcher-client/src/utils/personnelCache.js
const CACHE_PREFIX =
  "loadevo.personnel.v1";

const CACHE_TTL_MS =
  2 * 60 * 1000;

const CACHE_EVENT =
  "personnel:cache-changed";

const inFlightPersonnelLoads =
  new Map();

/* =========================================================
 * HELPERS
 * ======================================================= */

const safeParse = (
  value
) => {
  try {
    return value
      ? JSON.parse(
          value
        )
      : null;
  } catch {
    return null;
  }
};

const normalizeParams = (
  params = {}
) => {
  const result = {};

  Object.keys(
    params
  )
    .sort()
    .forEach(
      (
        key
      ) => {
        const value =
          params[key];

        if (
          value ===
            undefined ||
          value === null ||
          value === ""
        ) {
          return;
        }

        result[key] =
          value;
      }
    );

  return result;
};

/* =========================================================
 * CURRENT COMPANY
 * ======================================================= */

export const getPersonnelStoredUser =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return null;
    }

    const candidates = [
      window.sessionStorage.getItem(
        "user"
      ),

      window.sessionStorage.getItem(
        "auth_user"
      ),

      window.localStorage.getItem(
        "auth_user"
      ),

      window.localStorage.getItem(
        "user"
      ),
    ];

    for (
      const candidate
      of candidates
    ) {
      const parsed =
        safeParse(
          candidate
        );

      if (
        parsed &&
        typeof parsed ===
          "object"
      ) {
        return parsed;
      }
    }

    return null;
  };

export const getPersonnelCompanyId =
  () => {
    const user =
      getPersonnelStoredUser();

    const raw =
      user?.company_id ??
      user?.companyId ??
      user?.company?.id ??
      null;

    const companyId =
      Number(raw);

    if (
      !Number.isInteger(
        companyId
      ) ||
      companyId <= 0
    ) {
      return null;
    }

    return companyId;
  };

/* =========================================================
 * CACHE KEYS
 * ======================================================= */

export const getPersonnelCacheKey =
  (
    resource,
    params = {}
  ) => {
    const companyId =
      getPersonnelCompanyId();

    if (!companyId) {
      return null;
    }

    const normalized =
      normalizeParams(
        params
      );

    const signature =
      encodeURIComponent(
        JSON.stringify(
          normalized
        )
      );

    return [
      CACHE_PREFIX,
      companyId,
      resource,
      signature,
    ].join(":");
  };

/* =========================================================
 * READ
 * ======================================================= */

export const readPersonnelCache =
  (
    resource,
    params = {}
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return null;
    }

    const key =
      getPersonnelCacheKey(
        resource,
        params
      );

    if (!key) {
      return null;
    }

    try {
      const raw =
        window.sessionStorage.getItem(
          key
        );

      if (!raw) {
        return null;
      }

      const parsed =
        JSON.parse(raw);

      if (
        !parsed ||
        !Array.isArray(
          parsed.data
        )
      ) {
        return null;
      }

      const savedAt =
        Number(
          parsed.savedAt
        ) || 0;

      return {
        data:
          parsed.data,

        savedAt,

        isStale:
          !savedAt ||
          Date.now() -
            savedAt >
            CACHE_TTL_MS,
      };
    } catch (
      error
    ) {
      console.warn(
        "[personnelCache] read failed:",
        error
      );

      return null;
    }
  };

/* =========================================================
 * WRITE
 * ======================================================= */

export const writePersonnelCache =
  (
    resource,
    params = {},
    data = []
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return;
    }

    const key =
      getPersonnelCacheKey(
        resource,
        params
      );

    if (!key) {
      return;
    }

    try {
      window.sessionStorage.setItem(
        key,
        JSON.stringify({
          data:
            Array.isArray(
              data
            )
              ? data
              : [],

          savedAt:
            Date.now(),
        })
      );

      window.dispatchEvent(
        new CustomEvent(
          CACHE_EVENT,
          {
            detail: {
              resource,
              params:
                normalizeParams(
                  params
                ),
            },
          }
        )
      );
    } catch (
      error
    ) {
      console.warn(
        "[personnelCache] write failed:",
        error
      );
    }
  };

/* =========================================================
 * FRESHNESS
 * ======================================================= */

export const isPersonnelCacheFresh =
  (
    resource,
    params = {}
  ) => {
    const cached =
      readPersonnelCache(
        resource,
        params
      );

    return !!(
      cached &&
      !cached.isStale
    );
  };

  /* =========================================================
 * LOAD / REQUEST DEDUPLICATION
 *
 * One network request per exact:
 *
 * company + resource + params
 *
 * If Prefetch + Page Hook ask for the same resource
 * simultaneously, both await the same promise.
 * ======================================================= */

export const loadPersonnelResource =
  async ({
    resource,
    params = {},
    loader,
    force = false,
  }) => {
    if (
      typeof loader !==
      "function"
    ) {
      throw new Error(
        "Personnel resource loader is required."
      );
    }

    const key =
      getPersonnelCacheKey(
        resource,
        params
      );

    /*
     * No company available.
     *
     * Do not attempt to share/cache across an
     * unidentified session.
     */
    if (!key) {
      return loader();
    }

    /*
     * IMPORTANT:
     *
     * Check an existing request BEFORE checking
     * force/cache.
     *
     * This means:
     * - page hook
     * - layout prefetch
     * - sidebar prefetch
     *
     * can never perform the exact same GET
     * simultaneously.
     */
    const existing =
      inFlightPersonnelLoads.get(
        key
      );

    if (existing) {
      return existing;
    }

    if (!force) {
      const cached =
        readPersonnelCache(
          resource,
          params
        );

      if (
        cached &&
        !cached.isStale
      ) {
        return cached.data;
      }
    }

    let promise;

    promise =
      Promise.resolve()
        .then(
          () =>
            loader()
        )
        .then(
          (
            data
          ) => {
            const normalizedData =
              Array.isArray(
                data
              )
                ? data
                : [];

            writePersonnelCache(
              resource,
              params,
              normalizedData
            );

            return normalizedData;
          }
        )
        .finally(
          () => {
            if (
              inFlightPersonnelLoads.get(
                key
              ) ===
              promise
            ) {
              inFlightPersonnelLoads.delete(
                key
              );
            }
          }
        );

    inFlightPersonnelLoads.set(
      key,
      promise
    );

    return promise;
  };

/* =========================================================
 * MARK STALE
 *
 * Mantemos os dados visíveis.
 * Apenas obrigamos a refresh silencioso.
 * ======================================================= */

export const markPersonnelResourceStale =
  (
    resource
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return;
    }

    const companyId =
      getPersonnelCompanyId();

    if (!companyId) {
      return;
    }

    const prefix =
      [
        CACHE_PREFIX,
        companyId,
        resource,
        "",
      ].join(":");

    const keys = [];

    for (
      let index = 0;
      index <
      window.sessionStorage
        .length;
      index += 1
    ) {
      const key =
        window.sessionStorage.key(
          index
        );

      if (
        key &&
        key.startsWith(
          prefix
        )
      ) {
        keys.push(
          key
        );
      }
    }

    keys.forEach(
      (
        key
      ) => {
        try {
          const raw =
            window.sessionStorage.getItem(
              key
            );

          const parsed =
            JSON.parse(
              raw
            );

          window.sessionStorage.setItem(
            key,
            JSON.stringify({
              ...parsed,
              savedAt: 0,
            })
          );
        } catch {}
      }
    );

    window.dispatchEvent(
      new CustomEvent(
        CACHE_EVENT,
        {
          detail: {
            resource,
            stale: true,
          },
        }
      )
    );
  };

/* =========================================================
 * CLEAR
 * ======================================================= */

export const clearPersonnelCache =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return;
    }

    const companyId =
      getPersonnelCompanyId();

    if (!companyId) {
      return;
    }

    const prefix =
      `${CACHE_PREFIX}:${companyId}:`;

    const keys = [];

    for (
      let index = 0;
      index <
      window.sessionStorage
        .length;
      index += 1
    ) {
      const key =
        window.sessionStorage.key(
          index
        );

      if (
        key &&
        key.startsWith(
          prefix
        )
      ) {
        keys.push(
          key
        );
      }
    }

    keys.forEach(
      (
        key
      ) => {
        window.sessionStorage.removeItem(
          key
        );
      }
    );

    window.dispatchEvent(
      new CustomEvent(
        CACHE_EVENT,
        {
          detail: {
            cleared: true,
          },
        }
      )
    );
  };

/* =========================================================
 * SUBSCRIBE
 * ======================================================= */

export const subscribePersonnelCache =
  (
    callback
  ) => {
    if (
      typeof window ===
        "undefined" ||
      typeof callback !==
        "function"
    ) {
      return () => {};
    }

    const handler = (
      event
    ) => {
      callback(
        event?.detail ||
          {}
      );
    };

    window.addEventListener(
      CACHE_EVENT,
      handler
    );

    return () => {
      window.removeEventListener(
        CACHE_EVENT,
        handler
      );
    };
  };